import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import type { AnalysisFilters } from "./ApplicationAnalysisReport";

/**
 * The analysis report body.
 *
 * Replaces a single 23-section table that ran to several screens of identical
 * black header bars. Three things made it hard to read, and each is addressed
 * here rather than restyled:
 *
 *   1. ONE COLUMN. Twenty-three sections stacked vertically on a screen twice
 *      as wide as the content. They now flow in a two-column grid and collapse
 *      back to one for print, where a single column is correct.
 *   2. NO WAY IN. Nothing said what was in the report or let you reach it. A
 *      section index sits at the top, on screen only.
 *   3. LONG TAILS. Chapters and states run to dozens of rows each, which is
 *      most of the scrolling for a fraction of the meaning. On screen each card
 *      shows its top entries and opens on request; print always shows all of
 *      them, because a printed report cannot be expanded.
 *
 * Typography is the other half of it. The previous version set nearly every
 * string in black uppercase with wide tracking — when everything shouts,
 * nothing reads. Weight is now carried by the numbers, which is what the
 * report is actually for.
 */

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function pct(part: number, total: number): string {
  if (!total || !part) return "0%";
  const v = (part / total) * 100;
  // Whole numbers read faster; keep a decimal only where it carries meaning.
  return (v >= 10 ? v.toFixed(0) : v.toFixed(1)) + "%";
}

function safeObj(v: any): Record<string, number> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  return v as Record<string, number>;
}

function safeNum(v: any): number {
  return typeof v === "number" ? v : Number(v) || 0;
}

const num = (n: number) => n.toLocaleString();

type Entry = [string, number];

/** Section rows in the order they should read. */
function toEntries(
  data: Record<string, number>,
  opts?: { order?: string[]; sortDesc?: boolean; sortNumericKey?: boolean },
): Entry[] {
  const all = Object.entries(data).map(([k, v]) => [k, safeNum(v)] as Entry);

  if (opts?.order) {
    const known = opts.order
      .filter((k) => data[k] != null)
      .map((k) => [k, safeNum(data[k])] as Entry);
    const rest = all.filter(([k]) => !opts.order!.includes(k));
    return [...known, ...rest];
  }

  if (opts?.sortNumericKey) {
    return all.sort(
      ([a], [b]) => parseInt(a.split(/[-+ ]/)[0], 10) - parseInt(b.split(/[-+ ]/)[0], 10),
    );
  }

  if (opts?.sortDesc) return all.sort(([, a], [, b]) => b - a);

  return all;
}

/* ------------------------------------------------------------------ */
/* Atoms                                                                */
/* ------------------------------------------------------------------ */

/**
 * One headline figure. `tone` colours the number only — the card itself stays
 * neutral so five of them side by side do not read as five warnings.
 */
function Kpi({
  label,
  value,
  sub,
  tone = "neutral",
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: "neutral" | "primary" | "good" | "warn" | "bad";
}) {
  const toneClass = {
    neutral: "text-gray-900",
    primary: "text-indigo-600",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-rose-600",
  }[tone];

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 print:break-inside-avoid">
      <p className="text-[11px] font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold leading-none tabular-nums ${toneClass}`}>
        {num(value)}
      </p>
      {sub ? <p className="mt-1 text-[11px] text-gray-400">{sub}</p> : null}
    </div>
  );
}

/**
 * One row: what it is, how many, and its share — with the bar directly beneath
 * so the label has the full width of the card and never truncates a chapter
 * name into ambiguity.
 */
function Bar({ label, value, share, lead }: {
  label: string;
  value: number;
  share: number;
  lead?: boolean;
}) {
  const width = Math.max(share > 0 ? 1.5 : 0, Math.min(100, share));

  return (
    <li className="py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[13px] text-gray-700" title={label}>
          {label}
        </span>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums text-gray-900">
          {num(value)}
          <span className="ml-1.5 text-[11px] font-normal text-gray-400 tabular-nums">
            {share > 0 ? `${share >= 10 ? share.toFixed(0) : share.toFixed(1)}%` : "0%"}
          </span>
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${lead ? "bg-indigo-600" : "bg-indigo-400"}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </li>
  );
}

/**
 * A numbered section.
 *
 * `limit` caps the rows shown on screen. The remainder is rendered but hidden,
 * rather than omitted, so it is present when the page prints — the print popup
 * copies this markup verbatim and cannot click a "show all" button.
 */
function SectionCard({
  num: number_,
  title,
  entries,
  base,
  note,
  limit = 8,
}: {
  num: string;
  title: string;
  entries: Entry[];
  /** Denominator for the share column — the section's own total. */
  base?: number;
  note?: { label: string; value: number; of: number };
  limit?: number;
}) {
  const [open, setOpen] = useState(false);

  const subtotal = entries.reduce((s, [, v]) => s + v, 0);
  const denom = base ?? subtotal;
  const max = entries.reduce((m, [, v]) => Math.max(m, v), 0);

  const shown = open ? entries : entries.slice(0, limit);
  const hidden = open ? [] : entries.slice(limit);

  const row = ([key, val]: Entry) => (
    <Bar
      key={key}
      label={key}
      value={val}
      share={denom ? (val / denom) * 100 : 0}
      lead={val === max && val > 0}
    />
  );

  return (
    <section
      id={`sec-${number_}`}
      className="scroll-mt-24 rounded-xl border border-gray-200 bg-white print:break-inside-avoid print:mb-4"
    >
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h3 className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-900 text-[11px] font-bold text-white">
            {number_}
          </span>
          <span className="truncate text-[13px] font-semibold text-gray-900">{title}</span>
        </h3>
        <span className="shrink-0 text-[13px] font-bold tabular-nums text-gray-900">
          {num(subtotal)}
        </span>
      </header>

      <div className="px-4 py-2">
        {entries.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-gray-400">No data for this selection</p>
        ) : (
          <>
            {note ? (
              <div className="mb-2 flex items-baseline justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2">
                <span className="text-[12px] font-medium text-amber-900">{note.label}</span>
                <span className="text-[13px] font-bold tabular-nums text-amber-900">
                  {num(note.value)}
                  <span className="ml-1.5 text-[11px] font-normal text-amber-700">
                    {pct(note.value, note.of)}
                  </span>
                </span>
              </div>
            ) : null}

            <ul className="divide-y divide-gray-50">{shown.map(row)}</ul>

            {/* Present for print, folded away on screen. */}
            {hidden.length > 0 ? (
              <ul className="hidden divide-y divide-gray-50 print:block">{hidden.map(row)}</ul>
            ) : null}

            {entries.length > limit ? (
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="no-print mt-1 flex w-full items-center justify-center gap-1 rounded-lg py-2 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50 print:hidden"
              >
                {open ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" /> Show fewer
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" /> Show all {entries.length}
                  </>
                )}
              </button>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/** Jump links. Screen only — a printed page has no anchors to follow. */
function SectionIndex({ sections }: { sections: Array<{ num: string; title: string }> }) {
  return (
    <nav className="no-print mb-4 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 print:hidden">
      <p className="mb-2 text-[11px] font-semibold text-gray-500">Jump to section</p>
      <div className="flex flex-wrap gap-1.5">
        {sections.map((s) => (
          <a
            key={s.num}
            href={`#sec-${s.num}`}
            className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-600 transition-colors hover:border-indigo-300 hover:text-indigo-700"
          >
            <span className="font-semibold text-gray-400">{s.num}</span> {s.title}
          </a>
        ))}
      </div>
    </nav>
  );
}

function FilterChips({ filters }: { filters: AnalysisFilters }) {
  const active = Object.entries(filters).filter(([, v]) => Boolean(v));
  if (active.length === 0) return null;

  const labels: Record<string, string> = {
    status: "Status",
    year: "Year",
    month: "Month",
    age_range: "Age",
    gender: "Gender",
    whois: "Category",
    state_id: "State",
    chapter_id: "Chapter",
    sector_id: "Sector",
  };

  return (
    <div className="mt-2 flex flex-wrap justify-center gap-1.5">
      {active.map(([k, v]) => (
        <span
          key={k}
          className="rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
        >
          {labels[k] ?? k}: {v}
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Report                                                               */
/* ------------------------------------------------------------------ */
export function AnalysisReport({
  data,
  title,
  filters,
}: {
  data: any;
  title: string;
  filters: AnalysisFilters;
}) {
  const total = safeNum(data?.total ?? data?.meta?.total);

  const gender = safeObj(data?.gender);
  const statusMap = safeObj(data?.status);
  const category = safeObj(data?.category_breakdown);
  const memberGrades = safeObj(data?.membership_grades);
  const ageRanges = safeObj(data?.age_ranges);
  const maritalStatus = safeObj(data?.marital_status);
  const nationality = safeObj(data?.nationality);
  const geoZone = safeObj(data?.geographic_zone);
  const chapters = safeObj(data?.applications_by_chapter);
  const stateOrigin = safeObj(data?.applications_by_state_of_origin);
  const qualTypes = safeObj(data?.academic_qualifications);
  const qualClass = safeObj(data?.class_of_degree);
  const institutionType = safeObj(data?.training_institution_type);
  const employmentType = safeObj(data?.employment_type);
  const employmentSect = safeObj(data?.employment_sector);
  const expRanges = safeObj(data?.experience_ranges);
  const profMemberships = safeObj(data?.professional_memberships);
  const seminarStatus = safeObj(data?.seminar_status);
  const completionSteps = safeObj(data?.completion_steps);
  const yearlyTrend = safeObj(data?.yearly_submissions);
  const monthlySub = safeObj(data?.monthly_submissions);
  const whoisBreakdown = safeObj(data?.whois_breakdown);

  const maleNR = safeNum(gender.male ?? gender.Male);
  const femaleNR = safeNum(gender.female ?? gender.Female);
  const approvedNR = safeNum(statusMap.approved ?? statusMap.Approved);
  const pendingNR = safeNum(statusMap.pending ?? statusMap.Pending);
  const rejectedNR = safeNum(statusMap.rejected ?? statusMap.Rejected);

  const stepEntries = toEntries(completionSteps, { sortNumericKey: true });
  const fullyCompleted = stepEntries
    .filter(([k]) => parseInt(k, 10) >= 7)
    .reduce((s, [, v]) => s + v, 0);

  /**
   * Every section as data, so the index and the grid cannot fall out of step —
   * the previous version hand-numbered each one in JSX, which is how a section
   * gets renamed in one place and not the other.
   */
  const sections = useMemo(
    () => [
      {
        num: "1",
        title: "Application status",
        entries: toEntries(statusMap, {
          order: ["approved", "submitted", "acknowledged", "pending", "rejected"],
        }),
      },
      ...(Object.keys(whoisBreakdown).length > 0
        ? [
            {
              num: "2",
              title: "Applicant category",
              entries: toEntries(whoisBreakdown, {
                order: ["probationer", "student", "technician", "graduate"],
              }),
            },
          ]
        : []),
      { num: "3", title: "Category breakdown", entries: toEntries(category, { sortDesc: true }) },
      { num: "4", title: "Applicant membership grade", entries: toEntries(memberGrades) },
      { num: "5", title: "Age range", entries: toEntries(ageRanges, { sortNumericKey: true }) },
      {
        num: "6",
        title: "Gender",
        entries: toEntries(gender, {
          order: ["male", "Male", "female", "Female", "other", "Other"],
        }),
      },
      {
        num: "7",
        title: "Marital status",
        entries: toEntries(maritalStatus, {
          order: ["Single", "Married", "Divorced", "Widowed", "Other"],
        }),
      },
      { num: "8", title: "Nationality", entries: toEntries(nationality, { sortDesc: true }) },
      {
        num: "9",
        title: "Geographic zone",
        entries: toEntries(geoZone, {
          order: [
            "South-West", "South-East", "South-South",
            "North-West", "North-East", "North-Central", "FCT", "Other",
          ],
        }),
      },
      {
        num: "10",
        title: "By chapter",
        entries: toEntries(chapters, { sortDesc: true }),
        limit: 10,
      },
      {
        num: "11",
        title: "By state of residence",
        entries: toEntries(stateOrigin, { sortDesc: true }),
        limit: 10,
      },
      {
        num: "12",
        title: "Academic qualifications",
        entries: toEntries(qualTypes, {
          order: [
            "PHD", "M.SC", "M.TECH", "M.ENG", "PGD", "B.SC", "B.SC (HONS)",
            "B.TECH", "B.ENG", "HND", "OND", "NCE", "OTHER",
          ],
        }),
      },
      {
        num: "13",
        title: "Class of degree",
        entries: toEntries(qualClass, {
          order: [
            "FIRST CLASS", "DISTINCTION", "SECOND CLASS UPPER", "UPPER CREDIT",
            "CREDIT", "SECOND CLASS LOWER", "LOWER CREDIT", "MERIT",
            "THIRD CLASS", "PASS", "OTHER",
          ],
        }),
      },
      {
        num: "14",
        title: "Training institution type",
        entries: toEntries(institutionType, {
          order: ["University", "Polytechnic", "College", "Institute / School", "Other"],
        }),
      },
      {
        num: "15",
        title: "Employment type",
        entries: toEntries(employmentType, {
          order: ["Full-Time", "Part-Time", "Contract", "Self-Employed", "Unemployed", "Other"],
        }),
      },
      {
        num: "16",
        title: "Employment sector",
        entries: toEntries(employmentSect, {
          order: [
            "Public / Government", "Private", "Self-Employed / Consultancy",
            "NGO / International", "Academia / Research", "Other",
          ],
        }),
      },
      {
        num: "17",
        title: "Years of work experience",
        entries: toEntries(expRanges, {
          order: [
            "0-2 years", "3-5 years", "6-10 years",
            "11-15 years", "16-20 years", "20+ years",
          ],
        }),
      },
      {
        num: "18",
        title: "Professional body memberships",
        entries: toEntries(profMemberships, { sortDesc: true }),
      },
      {
        num: "19",
        title: "Seminar & CPD attendance",
        entries: toEntries(seminarStatus, { order: ["With Seminars", "No Seminars"] }),
      },
      {
        num: "20",
        title: "Completion stage",
        entries: stepEntries.map(([k, v]) => [`Step ${k}`, v] as Entry),
        note: { label: "Fully completed (step 7+)", value: fullyCompleted, of: total },
        limit: 12,
      },
      { num: "21", title: "Yearly submission trend", entries: toEntries(yearlyTrend) },
      { num: "22", title: "Monthly submissions", entries: toEntries(monthlySub), limit: 12 },
    ],
    [
      statusMap, whoisBreakdown, category, memberGrades, ageRanges, gender,
      maritalStatus, nationality, geoZone, chapters, stateOrigin, qualTypes,
      qualClass, institutionType, employmentType, employmentSect, expRanges,
      profMemberships, seminarStatus, stepEntries, fullyCompleted, total,
      yearlyTrend, monthlySub,
    ],
  );

  const generatedAt = data?.meta?.generated_at
    ? new Date(data.meta.generated_at).toLocaleString("en-GB", {
        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="text-gray-900">
      {/* ── Document header ── */}
      <header className="mb-5 border-b border-gray-200 pb-5 text-center">
        <p className="text-[11px] font-medium uppercase tracking-widest text-gray-400">
          Nigerian Institute of Quantity Surveyors
        </p>
        <h1 className="mt-1.5 text-lg font-bold text-gray-900">
          Analysis of Probationer Membership Applications
        </h1>
        <p className="mt-0.5 text-[13px] font-medium text-indigo-700">{title}</p>
        <p className="mt-1 text-[11px] text-gray-400">Generated {generatedAt}</p>
        <FilterChips filters={filters} />
      </header>

      {/* ── Headline figures ── */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 print:grid-cols-6">
        <Kpi label="Total applications" value={total} tone="primary" />
        <Kpi label="Approved" value={approvedNR} sub={pct(approvedNR, total)} tone="good" />
        <Kpi label="Pending" value={pendingNR} sub={pct(pendingNR, total)} tone="warn" />
        <Kpi label="Rejected" value={rejectedNR} sub={pct(rejectedNR, total)} tone="bad" />
        <Kpi label="Male" value={maleNR} sub={pct(maleNR, total)} />
        <Kpi label="Female" value={femaleNR} sub={pct(femaleNR, total)} />
      </div>

      <SectionIndex sections={sections} />

      {/* Two columns on screen, one for print — a printed page is narrow and a
          section split across a fold is worse than a longer document. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 print:block">
        {sections.map((s) => (
          <SectionCard
            key={s.num}
            num={s.num}
            title={s.title}
            entries={s.entries}
            note={(s as any).note}
            limit={(s as any).limit}
          />
        ))}
      </div>

      {/* ── Document footer ── */}
      <footer className="mt-8 border-t border-gray-200 pt-4 text-center">
        <p className="text-[11px] text-gray-400">
          Percentages are of each section's own total. Sections may not sum to the headline
          figure where a field was not answered.
        </p>
        <p className="mt-1.5 text-[11px] font-medium uppercase tracking-widest text-gray-400">
          NIQS Portal — Confidential
        </p>
      </footer>
    </div>
  );
}
